import {
    API_AVAILABLE_PRODUCT_PREFIX,
    API_CRM_INTERESTS_PREFIX,
    API_CRM_PRODUCTS_PREFIX,
    API_PRODUCT_PREFIX,
    API_PRODUCT_TYPES_PREFIX,
    API_USER_PRODUCT_PREFIX,
} from '@shared/config/api/api-prefixes';
import { productBaseApi } from './base';
import {
    CreateProductDataRequest,
    CreateProductDataResponse,
    EditProductDataRequest,
    EditProductDataResponse,
    ProductFullInfoDataRequest,
    ProductFullInfoDataResponse,
    ProductShortInfoDataRequest,
    ProductShortInfoDataResponse,
    ProductTypesListDataResponse,
    ProductsListDataRequest,
    ProductsListDataResponse,
    UserProductsListDataRequest,
    UserProductsListDataResponse,
    ProductInfoMessageDataRequest,
    ProductInfoMessageDataResponse,
    ProductTypesListDataRequest,
    CRMInterestsProductsDataResponse,
    CRMInterestsProductsDataRequest,
    CRMProductsDataResponse,
    CRMProductsDataRequest,
} from './product-types';
import { queryParametersToString } from '@shared/lib/utils/query-parameters-to-string/query-parameters-to-string';
import { objectToFormData } from '@shared/lib/utils/object-to-form-data/object-to-form-data';
import { isEqualParameters } from '@shared/lib/utils/is-equal-parameters/is-equal-parameters';

export const productApi = productBaseApi.injectEndpoints({
    endpoints: (build) => ({
        productAvailableList: build.query<ProductsListDataResponse, ProductsListDataRequest>({
            query: (parameters) => `${API_AVAILABLE_PRODUCT_PREFIX}${queryParametersToString({ ...parameters })}`,
            providesTags: ['ProductAvailableList'],
        }),
        userProductsList: build.query<UserProductsListDataResponse, UserProductsListDataRequest>({
            query: (parameters) => `${API_USER_PRODUCT_PREFIX}${queryParametersToString({ ...parameters })}`,
            providesTags: ['UserProductsList'],
        }),
        productAvailableInfinityList: build.query<ProductsListDataResponse, ProductsListDataRequest>({
            query: (parameters) => `${API_AVAILABLE_PRODUCT_PREFIX}${queryParametersToString({ ...parameters })}`,
            serializeQueryArgs: (params) => {
                return `${params.endpointName}_${params.queryArgs.search}`;
            },
            merge: (currentCache, newItems) => {
                if (newItems.skip) {
                    currentCache.items.push(...newItems.items);
                }
            },
            forceRefetch({ currentArg, previousArg }) {
                return !isEqualParameters({ currentParameters: currentArg, previousParameters: previousArg });
            },
        }),
        productShortInfo: build.query<ProductShortInfoDataResponse, ProductShortInfoDataRequest>({
            query: (id) => `${API_PRODUCT_PREFIX}/${id}`,
        }),
        productFullInfo: build.query<ProductFullInfoDataResponse, ProductFullInfoDataRequest>({
            query: (id) => `${API_PRODUCT_PREFIX}/${id}/full`,
        }),
        productTypesList: build.query<ProductTypesListDataResponse, ProductTypesListDataRequest>({
            query: (parameters) => `${API_PRODUCT_TYPES_PREFIX}${queryParametersToString({ ...parameters })}`,
            providesTags: ['ProductTypesList'],
        }),
        productTypesInfinityList: build.query<ProductTypesListDataResponse, ProductTypesListDataRequest>({
            query: (parameters) => `${API_PRODUCT_TYPES_PREFIX}${queryParametersToString({ ...parameters })}`,
            serializeQueryArgs: (params) => {
                return `${params.endpointName}_${params.queryArgs.search}`;
            },
            merge: (currentCache, newItems) => {
                if (newItems.skip) {
                    currentCache.items.push(...newItems.items);
                }
            },
            forceRefetch({ currentArg, previousArg }) {
                return !isEqualParameters({ currentParameters: currentArg, previousParameters: previousArg });
            },
        }),
        adminProductList: build.query<ProductsListDataResponse, ProductsListDataRequest>({
            query: (parameters) => `${API_PRODUCT_PREFIX}${queryParametersToString({ ...parameters })}`,
            providesTags: ['ProductsList'],
        }),
        adminProductInfinityList: build.query<ProductsListDataResponse, ProductsListDataRequest>({
            query: (parameters) => `${API_PRODUCT_PREFIX}${queryParametersToString({ ...parameters })}`,
            serializeQueryArgs: (params) => {
                return `${params.endpointName}_${params.queryArgs.search}`;
            },
            merge: (currentCache, newItems) => {
                if (newItems.skip) {
                    currentCache.items.push(...newItems.items);
                }
            },
            forceRefetch({ currentArg, previousArg }) {
                return !isEqualParameters({ currentParameters: currentArg, previousParameters: previousArg });
            },
        }),
        updateProduct: build.mutation<EditProductDataResponse, EditProductDataRequest>({
            query: (data) => {
                return {
                    url: `${API_PRODUCT_PREFIX}/${data.id}`,
                    method: 'POST',
                    body: objectToFormData(data),
                    headers: {
                        'Content-Type': 'multipart/form-data;',
                    },
                };
            },
            invalidatesTags: ['ProductsList'],
        }),
        createProduct: build.mutation<CreateProductDataResponse, CreateProductDataRequest>({
            query: (data) => {
                return {
                    url: API_PRODUCT_PREFIX,
                    method: 'POST',
                    body: objectToFormData(data),
                    headers: {
                        'Content-Type': 'multipart/form-data;',
                    },
                };
            },
            invalidatesTags: ['ProductsList'],
        }),
        productInfoMessage: build.query<ProductInfoMessageDataResponse, ProductInfoMessageDataRequest>({
            query: (parameters) =>
                `${API_PRODUCT_PREFIX}/${parameters.id}/message${queryParametersToString({ ...parameters })}`,
            providesTags: ['ProductInfoMessage'],
        }),
        crmInterestsProductsInfinityList: build.query<
            CRMInterestsProductsDataResponse,
            CRMInterestsProductsDataRequest
        >({
            query: (parameters) => `${API_CRM_INTERESTS_PREFIX}${queryParametersToString({ ...parameters })}`,
            serializeQueryArgs: (params) => {
                return `${params.endpointName}_${params.queryArgs.search}`;
            },
            merge: (currentCache, newItems) => {
                if (newItems.skip) {
                    currentCache.items.push(...newItems.items);
                }
            },
            forceRefetch({ currentArg, previousArg }) {
                return !isEqualParameters({ currentParameters: currentArg, previousParameters: previousArg });
            },
        }),
        crmProductsInfinityList: build.query<CRMProductsDataResponse, CRMProductsDataRequest>({
            query: (parameters) => `${API_CRM_PRODUCTS_PREFIX}${queryParametersToString({ ...parameters })}`,
            serializeQueryArgs: (params) => {
                return `${params.endpointName}_${params.queryArgs.search}`;
            },
            merge: (currentCache, newItems) => {
                if (newItems.skip) {
                    currentCache.items.push(...newItems.items);
                }
            },
            forceRefetch({ currentArg, previousArg }) {
                return !isEqualParameters({ currentParameters: currentArg, previousParameters: previousArg });
            },
        }),
    }),
});

export const {
    useProductTypesInfinityListQuery,
    useLazyProductAvailableListQuery,
    useLazyProductShortInfoQuery,
    useLazyProductFullInfoQuery,
    useLazyUserProductsListQuery,
    useAdminProductListQuery,
    useAdminProductInfinityListQuery,
    useLazyAdminProductListQuery,
    useUpdateProductMutation,
    useCreateProductMutation,
    usePrefetch: usePrefetchProduct,
    useLazyProductInfoMessageQuery,
    useLazyProductTypesListQuery,
    useLazyProductAvailableInfinityListQuery,
    useCrmInterestsProductsInfinityListQuery,
    useCrmProductsInfinityListQuery,
} = productApi;
